import { useEffect, useRef, useState } from "react"

import { generateHTML } from "@tiptap/html"
import StarterKit from "@tiptap/starter-kit"
import { atom, useAtom } from "jotai"
import { RESET, atomWithDefault } from "jotai/utils"
import { camelCase, isEqual, omit } from "lodash"
import { useRouter } from "next/router"
import { FormProvider, useForm } from "react-hook-form"
import { v4 as uuidv4 } from "uuid"

import Alert from "components/Alert"
import { ActionButton, Button } from "components/Button"
import AvailabilityCalendar from "components/dashboard/calendar"
import Variants from "components/ecom/product/booking/Variants"
import FaIcon from "components/FontAwesomeIcon"
import { Switch } from "components/forms/FormElements"
import { Container, Section } from "components/Layout"
import { Heading } from "components/Text"
import useResponsive from "hooks/useResponsive"
import { productPagePropsAtom } from "pages/products/[slug]"
import { addToCartAtom, cartAtom } from "store"
import { formatDateForDisplay } from "utils/dates"
import { ScrollTrigger } from "utils/gsap"
import { slugify } from "utils/helpers"
import { formatAmountForDisplay } from "utils/stripe"

import AddonComponent from "../Addon"
import HelpText from "../HelpText"

import AddonSummaryItem from "./AddonSummaryItem"
import AddonTemplateDoorSensors from "./AddonTemplateDoorSensors"
import AddonTemplatePirs from "./AddonTemplatePirs"
import InstallTerms from "./InstallTerms"
import ProgressBar, { ProgressBarItem } from "./ProgressBar"
import TotalsTable from "./TotalsTable"

import type {
  Product,
  ProductAddonTemplate,
  ProductAddonType,
  ProductCrossSell
} from "@prisma/client"
import type { JSONContent } from "@tiptap/react"
import type {
  ProductAddonWithImages,
  ProductVariantWithImagesAndAddons
} from "pages/products/[slug]"
import type { ParsedUrlQuery } from "querystring"
import type { UseFormReturn } from "react-hook-form"

type Step = {
  title: string
  content: JSONContent
  helpText: JSONContent
}
type StepState = Step & {
  // can be undefined when resetting that atoms
  navigation?: "current" | "disabled" | "allowed"
  type?: "default" | "variant" | "template"
}

const defaultFormValuesAtom = atomWithDefault(get => {
  const productPageProps = get(productPagePropsAtom)
  const totalVariantSteps = get(totalVariantStepsAtom)
  const templateAddons = get(templateAddonsAtom)
  const addonsWithPirTemplate = templateAddons?.filter(
    addon => addon.template === "PIRS"
  )
  const addonsWithDoorSensorTemplate = templateAddons?.filter(
    addon => addon.template === "DOORSENSORS"
  )

  return {
    variant:
      totalVariantSteps === 0
        ? {
            0: {
              id: productPageProps?.product.variants[0]?.id || "",
              title: productPageProps?.product.variants[0]?.title || ""
            }
          }
        : {},
    addons: {
      // Set the global addon defaults. Can't use the selectedVariantAddons yet as they are not set until the user has selected a variant. We register the variant template addons though as they dont use dynamic values & am registering above
      // register the global addons except if the addon type is TEMPLATE as they are registered above
      ...productPageProps?.product.addons.reduce((acc, addon) => {
        if (addon.type === "TEMPLATE") {
          return acc
        }
        return {
          ...acc,
          [addon.id]: {
            id: addon.id,
            title: addon.title,
            quantity: 0,
            type: addon.type,
            template: addon.template
          }
        }
      }, {}),
      // if any addonsWithPirTemplate, register them
      ...(addonsWithPirTemplate && addonsWithPirTemplate.length > 0
        ? addonsWithPirTemplate.reduce((acc, addon) => {
            return {
              ...acc,
              [addon.id]: {
                id: addon.id,
                title: ["Hallway", "Living room"],
                quantity: 2,
                type: addon.type,
                template: addon.template
              }
            }
          }, {})
        : {}),
      ...(addonsWithDoorSensorTemplate &&
      addonsWithDoorSensorTemplate.length > 0
        ? addonsWithDoorSensorTemplate.reduce((acc, addon) => {
            return {
              ...acc,
              [addon.id]: {
                id: addon.id,
                title: ["Front door"],
                quantity: 1,
                type: addon.type,
                template: addon.template
              }
            }
          }, {})
        : {})
    },
    agreedTerms: false,
    installDate: "",
    crossSells: []
  }
})

// set URL params when the page loads into this atom
export const urlParamsAtom = atom<ParsedUrlQuery | null>(null)

// use the cartAtom, productPageProps & urlParamsAtom to get the current crossSell from the cart by matching the urlParam crossSellId with the cart crossSellToId
export const currentCrossSellAtom = atom(get => {
  const urlParams = get(urlParamsAtom)
  if (!urlParams?.crossSellId || !urlParams?.uuid) return null
  const cart = get(cartAtom)
  const productPageProps = get(productPagePropsAtom)

  // if the crossSellId provided to the url param is in the cart, return the crossSellTo from the productPageProps?.product.crossSellsTo array
  const foundCrossSell = cart
    .map(
      ci =>
        ci.crossSells &&
        ci.crossSells.find(
          c => c.id === urlParams.crossSellId && c.uuid === urlParams.uuid
        )
    )
    .find(c => c)

  if (foundCrossSell) {
    return productPageProps?.product.crossSellsTo.find(
      c => c.id === foundCrossSell.id
    )
  }
  return null
})

// get the template addons from the pagePropsAtom
export const templateAddonsAtom = atomWithDefault<
  ProductAddonWithImages[] | null
>(get => {
  const pageProps = get(productPagePropsAtom)
  if (!pageProps) return []

  const { addons, variants } = pageProps.product

  const getTemplateAddons = () => {
    const templateAddons = [] as ProductAddonWithImages[]

    // first check the products global addons
    addons.forEach(addon => {
      if (addon.template === "PIRS" || addon.template === "DOORSENSORS") {
        templateAddons.push(addon)
      }
    })

    // then iterate recursively through the variant levels and check for templated addons
    const checkVariantAddons = (
      variants: ProductVariantWithImagesAndAddons[]
    ) => {
      if (!variants) return
      variants.forEach(variant => {
        if (variant.addons) {
          variant.addons.forEach(addon => {
            if (addon.template === "PIRS" || addon.template === "DOORSENSORS") {
              templateAddons.push(addon)
            }
          })
        }
      })
    }

    checkVariantAddons(variants)

    return templateAddons
  }

  return getTemplateAddons()
})

// use the pagePropsAtom to create a new atom containing the step data
export const getStepsAtom = atom(get => {
  const props = get(productPagePropsAtom)
  const isCrossSell = !!get(currentCrossSellAtom)
  if (!props) return [] as StepState[]

  const { product, optionalExtrasContent, installTimeContent } = props

  // first create the variant steps
  const variantSteps = product.steps
    ? JSON.parse(product.steps).map((step: Step, i: number) => ({
        title: step.title,
        content: step.content, // <-- Already parsed 2 lines up unlike the others!
        helpText: step.helpText,
        navigation: i === 0 ? "current" : "disabled",
        type: "variant"
      }))
    : []

  // then create the template steps
  const templateSteps =
    get(templateAddonsAtom)?.map((addon, i) => ({
      title: addon.title,
      content: JSON.parse(addon.content || ""),
      // if no variant steps, make the first templateStep "current"
      navigation:
        variantSteps.length === 0 && i === 0
          ? "current"
          : variantSteps.length === 0 && i === 1
          ? "allowed"
          : "disabled",
      type: "template"
    })) || []

  const hasTermsStep =
    product.title.toLowerCase().includes("cctv") ||
    product.title.toLowerCase().includes("alarm")

  // then create the default steps
  const defaultSteps = [
    {
      title: "Optional Extras",
      content: JSON.parse(optionalExtrasContent || ""),
      // if no variant or template steps, make this step "current"
      navigation:
        variantSteps.length === 0 && templateSteps.length === 0
          ? "current"
          : "disabled",
      type: "default"
    },
    ...(hasTermsStep
      ? [
          {
            title: "Install Terms",
            navigation:
              variantSteps.length === 0 && templateSteps.length === 0
                ? "allowed"
                : "disabled",
            type: "default"
          }
        ]
      : []),
    ...(!isCrossSell
      ? [
          {
            title: "Install Date",
            content: JSON.parse(installTimeContent || ""),
            navigation:
              variantSteps.length === 0 && templateSteps.length === 0
                ? "allowed"
                : "disabled",
            type: "default"
          }
        ]
      : [])
  ]

  // finally, return the steps in the correct order
  return [...variantSteps, ...templateSteps, ...defaultSteps] as StepState[]
})

// create a primitive atom that is readable & writable from what we get from the getStepsAtom
export const stepsAtom = atomWithDefault(get => get(getStepsAtom))

export const setStepsAtom = atom(
  null,
  (
    get,
    set,
    {
      stepTo,
      rhf,
      event
    }: {
      stepTo: number
      rhf: UseFormReturn<ProductTypeBookingForm>
      event?: React.MouseEvent<HTMLElement, MouseEvent>
    }
  ) => {
    const steps = get(stepsAtom)
    const currentStep = get(currentStepAtom)
    const totalVariantSteps = get(totalVariantStepsAtom)
    const totalTemplateSteps = get(totalTemplateStepsAtom)
    const totalUserDefinedSteps = totalVariantSteps + totalTemplateSteps - 1
    const selectedVariantAddons = get(selectedVariantAddonsAtom)

    // if step to is less than 0 or greater than totalSteps, return early
    if (stepTo < 0 || stepTo > steps.length) return

    // if currentStep === stepTo, do nothing
    if (currentStep === stepTo) return

    // If stepTo is less than totalVariantSteps need to reset the variant choices on the any step between stepTo and currentStep to avoid allowing selection of sub-variants of the previous variant. Do this in RHF, and set the in between steps to "disabled"
    if (stepTo < totalUserDefinedSteps && currentStep > stepTo) {
      Object.keys(rhf.watch("variant")).forEach((_, i) => {
        if (i > stepTo && i <= currentStep) {
          rhf.setValue("variant", omit(rhf.watch("variant"), i))
          steps[i].navigation = "disabled"
        }
      })
    }

    // if currentStep is totalUserDefinedSteps + 1 & stepTo is totalUserDefinedSteps (the optional extras step), unregister the selectedVariantAddons
    if (
      currentStep === totalUserDefinedSteps + 1 &&
      stepTo === totalUserDefinedSteps
    ) {
      Object.values(rhf.watch("addons")).forEach(addon => {
        selectedVariantAddons.map(a => {
          if (a.id === addon.id) {
            rhf.unregister(`addons.${addon.id}`)
            // rhf.setValue("addons", omit(rhf.watch("addons"), addon.id))
          }
        })
      })
    }

    // if currentStep is totalUserDefinedSteps & stepTo is totalUserDefinedSteps + 1 (the optional extras step), register the selectedVariantAddons and set the default values. Need this here to re-register the addons when going back to the optional extras step
    if (
      currentStep === totalUserDefinedSteps &&
      stepTo === totalUserDefinedSteps + 1
    ) {
      selectedVariantAddons.map(a => {
        rhf.setValue(`addons.${a.id}`, {
          title: a.title,
          quantity: 0,
          id: a.id,
          // set type and template so can access in cart
          type: a.type,
          template: a.template
        })
      })
    }

    // create a clone of the steps array to see if it has changed later
    const stepsClone = [...steps]

    // handle whether user is allowed to click the step or not by checking if it's allowed in the onClick handler. Then below set the navigation state based on the stepTo
    set(
      stepsAtom,
      steps.map((step, stepIndex) =>
        // if the stepIndex is the step we want to stepTo, set to "current"
        stepIndex === stepTo
          ? { ...step, navigation: "current" as const }
          : // if the step we want to stepTo is a variant and the stepIndex is greater than the step we want to stepTo it should be disabled
          stepTo < totalVariantSteps - 1 &&
            stepIndex > stepTo &&
            step.type !== "variant"
          ? { ...step, navigation: "disabled" as const }
          : stepTo > totalVariantSteps - 1 && step.type !== "variant"
          ? { ...step, navigation: "allowed" as const }
          : stepIndex === currentStep && stepTo > currentStep
          ? { ...step, navigation: "allowed" as const }
          : stepIndex === currentStep && stepTo < currentStep
          ? { ...step, navigation: "disabled" as const }
          : step
      )
    )

    // if step changed, remove focus from trigger and scroll to top
    if (stepsClone !== steps && event) {
      event.currentTarget.blur()
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 0)
    }
  }
)

export const currentStepAtom = atom<number>(get => {
  const index = get(stepsAtom).findIndex(
    state => state.navigation === "current"
  )
  return index === -1 ? 0 : index
})

// NOTE returns the length. The steps are like 0, 1, 2 and this is 1, 2, 3 - Need to do this as the steps are 0 based and if I do -1 on a 0 length it will return -1 which wont work
export const totalVariantStepsAtom = atom<number>(get => {
  return get(stepsAtom).filter(state => state.type === "variant").length
})

export const totalTemplateStepsAtom = atom<number>(get => {
  return get(stepsAtom).filter(state => state.type === "template").length
})

// if no variants are attached, set the priceAtom to the price of the first available variant
export const priceAtom = atomWithDefault(get => {
  const productPageProps = get(productPagePropsAtom)
  const totalVariantSteps = get(totalVariantStepsAtom)

  // if there are templated addons attached with pre-filled values, get them & their prices
  const templateAddons = get(templateAddonsAtom)
  let addonsPrice = 0
  if (templateAddons) {
    templateAddons.forEach(addon => {
      // 2 pirs by default so multiply the price by 2
      if (addon.template === "PIRS") {
        addonsPrice += (addon.price || 0) * 2
      }
      // 1 door sensor by default so multiply the price by 1
      else if (addon.template === "DOORSENSORS") {
        addonsPrice += (addon.price || 0) * 1
      }
      // otherwise don't do anything as nothing else is preset so can calculate in the client
    })
  }

  if (totalVariantSteps === 0) {
    return {
      variant: productPageProps?.product.variants[0]?.price || 0,
      addons: addonsPrice
    }
  } else {
    return {
      variant: 0,
      addons: addonsPrice
    }
  }
})

// if no variants are attached set the setSelectedVariantAddons to the addons on the first available variant
export const selectedVariantAddonsAtom = atomWithDefault(get => {
  const productPageProps = get(productPagePropsAtom)
  const totalVariantSteps = get(totalVariantStepsAtom)
  if (totalVariantSteps === 0)
    return productPageProps?.product.variants[0]?.addons || []
  return []
})

export type ProductTypeBookingForm = {
  variant: Record<string, { id: string; title: string }>
  // allow addons titles to be either a string or an array of strings - If addon is a template, it needs an array of titles
  addons: Record<
    string,
    {
      id: string
      title: string | string[]
      quantity: number
      type: ProductAddonType
      template: ProductAddonTemplate
    }
  >
  agreedTerms: boolean
  installDate: string // Date.toISOString()
  crossSells: {
    id: ProductCrossSell["id"]
    crossSellFromId: ProductCrossSell["crossSellFromId"]
    crossSellFromTitle: Product["title"]
    crossSellToId: ProductCrossSell["crossSellToId"]
    crossSellToTitle: Product["title"]
    hasCompleted: boolean
    uuid: string // <-- Might have multiple products with the same crossSell id. Add another uuid to make it unique
  }[]
}

const ProductTypeBooking: React.FC = () => {
  const router = useRouter()
  const responsive = useResponsive()
  const [productPageProps] = useAtom(productPagePropsAtom)
  const [urlParams, setUrlParams] = useAtom(urlParamsAtom)
  const [currentCrossSell] = useAtom(currentCrossSellAtom)
  const isCrossSell = !!currentCrossSell
  const [price, setPrice] = useAtom(priceAtom)
  const [selectedVariantAddons, setSelectedVariantAddons] = useAtom(
    selectedVariantAddonsAtom
  )
  const [steps, resetSteps] = useAtom(stepsAtom)
  const [currentStep] = useAtom(currentStepAtom)
  const [defaultFormValues, setDefaultFormValues] = useAtom(
    defaultFormValuesAtom
  )
  const [, setSteps] = useAtom(setStepsAtom)
  const [totalVariantSteps] = useAtom(totalVariantStepsAtom)
  const [totalTemplateSteps] = useAtom(totalTemplateStepsAtom)
  const [templateAddons, setTemplateAddons] = useAtom(templateAddonsAtom)
  const [, addToCart] = useAtom(addToCartAtom)
  const [cart, setCart] = useAtom(cartAtom)
  const productSteps = (
    productPageProps?.product.steps
      ? JSON.parse(productPageProps?.product.steps)
      : []
  ) as Step[]

  // create refs for the right and left for the scrollTrigger
  const leftContentRef = useRef<HTMLDivElement>(null)
  const rightContentRef = useRef<HTMLDivElement>(null)
  const [addingToCart, setAddingToCart] = useState<boolean>(false)

  // calculate amount of steps from max depth of variants. Also take any addons where the user has chosen a templated option into account.
  const totalUserDefinedSteps = totalVariantSteps + totalTemplateSteps - 1
  const totalSteps = steps.length - 1

  const addonsWithPirTemplate = templateAddons?.filter(
    addon => addon.template === "PIRS"
  )
  const addonsWithDoorSensorTemplate = templateAddons?.filter(
    addon => addon.template === "DOORSENSORS"
  )

  const rhf = useForm<ProductTypeBookingForm>({
    mode: "onTouched",
    defaultValues: defaultFormValues
  })

  useEffect(() => {
    if (responsive.isLgMin) {
      ScrollTrigger.create({
        trigger: leftContentRef.current,
        start: "top top+=150",
        end: self => {
          const trigger = self.trigger as HTMLElement
          const pin = self.pin as HTMLElement
          return "+=" + (trigger.offsetHeight - pin.offsetHeight)
        },
        scrub: true,
        pin: rightContentRef.current
      })
    } else {
      ScrollTrigger.getAll().forEach(st => st.kill())
    }

    return () => {
      ScrollTrigger.getAll().forEach(st => st.kill())
    }
  }, [responsive.isLgMin])

  // using rhf subscription to update the addon price and refresh the scrolltrigger when something changes on the form. Can't use watch as a dep. See example on this page - https://react-hook-form.com/api/useform/watch
  useEffect(() => {
    const subscription = rhf.watch(fields => {
      // get the addon prices from the global addons and the selected variant addons
      const addons = [productPageProps?.product.addons, selectedVariantAddons]
      let addonPrice = 0
      addons.flat().forEach(addon => {
        if (!addon) return
        const price = addon?.price || 0
        const quantity = fields?.addons?.[addon?.id]?.quantity || 0
        addonPrice += price * quantity
      })

      setPrice(prev => ({
        ...prev,
        addons: addonPrice
      }))

      // refresh on input change
      setTimeout(() => {
        ScrollTrigger.refresh()
      }, 0)
    })

    // refresh on step change
    ScrollTrigger.refresh()

    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, rhf.watch])

  useEffect(() => {
    // when the url params change, set the url params atom
    setUrlParams(router.query)
    // and reset the required atoms in this order...
    resetSteps(RESET)
    setSelectedVariantAddons(RESET)
    setTemplateAddons(RESET)
    setDefaultFormValues(RESET)
    setPrice(RESET)
    // not changing price so set again TODO FIX
    setTimeout(() => {
      setPrice(RESET)
    }, 1000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router])

  // reset the form state when defaultFormValues change
  useEffect(() => {
    rhf.reset(defaultFormValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFormValues])

  return (
    <FormProvider {...rhf}>
      <Section className="!pb-0">
        <Container>
          <Heading alignment="center">
            {productPageProps?.product.title}
          </Heading>
          {productPageProps?.product.content && (
            <div
              className="text-center"
              dangerouslySetInnerHTML={{
                __html: generateHTML(
                  JSON.parse(productPageProps.product.content),
                  [StarterKit]
                )
              }}
            />
          )}
        </Container>
      </Section>
      <Section className="!pb-0">
        <Container>
          <ProgressBar step={currentStep} steps={totalSteps}>
            {/* The variant steps */}
            {productSteps.map((step, index) => (
              <ProgressBarItem
                key={`variant-step-${index}`}
                // show disabled state on the variant steps when it is greater than the currentStep
                disabled={
                  steps[index].navigation === "disabled" ||
                  (index > currentStep && !rhf.formState.isValid)
                }
                onClick={e => {
                  if (
                    steps[index].navigation !== "allowed" ||
                    (index > currentStep && !rhf.formState.isValid)
                  ) {
                    return
                  }
                  setSteps({
                    event: e,
                    stepTo: index,
                    rhf: rhf
                  })
                }}
              >
                {/* Break words onto different lines */}
                <span className="smmax:block">{index + 1}. </span>
                {step.title.split(" ").map((word, index) => (
                  <span
                    key={`variant-step-word-${index}`}
                    className={index > 0 ? "block" : ""}
                  >
                    {word}
                  </span>
                ))}
              </ProgressBarItem>
            ))}
            {/* The template addons steps */}
            {templateAddons?.map((addon, index) => (
              <ProgressBarItem
                key={`addon-step-${index}`}
                // show disabled state on the addons when the variants are not complete
                disabled={
                  steps[index].navigation === "disabled" ||
                  (index > currentStep && !rhf.formState.isValid)
                }
                onClick={e => {
                  if (
                    steps[index].navigation !== "allowed" ||
                    (index > currentStep && !rhf.formState.isValid)
                  ) {
                    return
                  }
                  setSteps({
                    event: e,
                    stepTo: index,
                    rhf: rhf
                  })
                }}
              >
                {/* Break words onto different lines */}
                <span className="smmax:block">
                  {index + 1 + totalVariantSteps}.{" "}
                </span>
                {addon.title.split(" ").map((word, index) => (
                  <span
                    key={`addon-step-word-${index}`}
                    className={index > 0 ? "block" : ""}
                  >
                    {word}
                  </span>
                ))}
              </ProgressBarItem>
            ))}
            {/* The optional extras (other addons) step */}
            <ProgressBarItem
              disabled={
                steps[totalSteps - 2]?.navigation === "disabled" ||
                (totalSteps - 2 > currentStep && !rhf.formState.isValid)
              }
              onClick={e => {
                if (
                  steps[totalSteps - 2]?.navigation !== "allowed" ||
                  (totalSteps - 2 > currentStep && !rhf.formState.isValid)
                ) {
                  return
                }
                setSteps({
                  event: e,
                  stepTo: totalSteps - 2,
                  rhf: rhf
                })
              }}
            >
              <span className="smmax:block">{totalSteps - 1}.</span> Optional{" "}
              <span className="block">Extras</span>
            </ProgressBarItem>
            {/* The terms step */}
            <ProgressBarItem
              disabled={
                steps[totalSteps - 1]?.navigation === "disabled" ||
                (totalSteps - 1 > currentStep && !rhf.formState.isValid)
              }
              onClick={e => {
                if (
                  steps[totalSteps - 1]?.navigation !== "allowed" ||
                  (totalSteps - 1 > currentStep && !rhf.formState.isValid)
                ) {
                  return
                }
                setSteps({
                  event: e,
                  stepTo: totalSteps - 1,
                  rhf: rhf
                })
              }}
            >
              <span className="smmax:block">{totalSteps}.</span> Install{" "}
              <span className="block">Terms</span>
            </ProgressBarItem>
            {/* The install date (last step) */}
            {!isCrossSell && (
              <ProgressBarItem
                disabled={
                  steps[totalSteps]?.navigation === "disabled" ||
                  (totalSteps > currentStep && !rhf.formState.isValid)
                }
                onClick={e => {
                  if (
                    steps[totalSteps]?.navigation !== "allowed" ||
                    (totalSteps > currentStep && !rhf.formState.isValid)
                  ) {
                    return
                  }
                  setSteps({
                    event: e,
                    stepTo: totalSteps,
                    rhf: rhf
                  })
                }}
              >
                <span className="smmax:block">{totalSteps + 1}.</span> Install
                Date
              </ProgressBarItem>
            )}
          </ProgressBar>
        </Container>
      </Section>

      {isCrossSell && (
        <Container>
          <Alert show variant="success">
            <p className="my-0 text-sm">
              You chose to add {productPageProps?.product.title} to your booking
              along with {currentCrossSell?.crossSellFrom?.title} for a discount
              of{" "}
              {currentCrossSell?.discountType === "PERCENTAGE"
                ? (currentCrossSell.discountPercentage || 0) + "%"
                : formatAmountForDisplay(
                    currentCrossSell?.discountAmount || 0,
                    "gbp"
                  )}{" "}
              when booked together.
            </p>
            <p className="my-0 text-sm">
              Don&rsquo;t want to add {productPageProps?.product.title}?{" "}
              <span
                className="underline cursor-pointer"
                onClick={() => {
                  const crossSellToRemoveFromCart = {
                    id: currentCrossSell.id, // <-- The crossSell ID
                    crossSellFromId: currentCrossSell.crossSellFromId, // <-- The product id - same as productPageProps.product.id
                    crossSellFromTitle: productPageProps?.product.title,
                    crossSellToId: currentCrossSell.crossSellToId, // <-- The crossSellTo product id
                    crossSellToTitle:
                      currentCrossSell.crossSellFrom?.title || ""
                  }
                  // run through the cart array and each cart items crossSells array & look for a matching crossSell to remove
                  const newCart = cart.map(cartItem => {
                    if (!cartItem.crossSells) return cartItem
                    const newCrossSells = cartItem.crossSells.filter(
                      crossSell => isEqual(crossSell, crossSellToRemoveFromCart)
                    )
                    return {
                      ...cartItem,
                      crossSells: newCrossSells
                    }
                  })
                  setCart(newCart)
                  router.push("/checkout")
                }}
              >
                Click here to skip
              </span>
            </p>
          </Alert>
        </Container>
      )}

      <Section>
        <Container className="flex flex-wrap gap-6 items-start">
          <div className="flex-1 relative smmax:pt-6" ref={leftContentRef}>
            {steps[currentStep]?.title && (
              <Heading>{steps[currentStep].title}</Heading>
            )}

            <div
              dangerouslySetInnerHTML={{
                __html: steps[currentStep]?.content
                  ? generateHTML(steps[currentStep].content, [StarterKit])
                  : ""
              }}
            />

            {steps[currentStep]?.helpText && (
              <HelpText
                variant="absolute"
                question="Not sure which one?"
                helpContent={steps[currentStep]?.helpText}
              />
            )}

            {productPageProps?.product.variants && totalVariantSteps > 0 && (
              <Variants variants={productPageProps?.product.variants} />
            )}

            {/* show the templateAddon index from the templateAddons array using the currentStep index */}
            {/* Map through addonsWithPirTemplate & display the addons */}
            {currentStep >= totalVariantSteps &&
            currentStep <= totalUserDefinedSteps ? (
              <>
                {templateAddons?.[currentStep - totalVariantSteps]?.template ===
                  "PIRS" &&
                  addonsWithPirTemplate?.map(addon => (
                    <AddonTemplatePirs addon={addon} key={addon.id} />
                  ))}
                {templateAddons?.[currentStep - totalVariantSteps].template ===
                  "DOORSENSORS" &&
                  addonsWithDoorSensorTemplate?.map(addon => (
                    <AddonTemplateDoorSensors addon={addon} key={addon.id} />
                  ))}
              </>
            ) : null}

            {currentStep ===
              (isCrossSell ? totalSteps - 1 : totalSteps - 2) && (
              // Use productPageProps?.product.addons & selectedVariantAddons to generate the optional extras (addons)
              <div className="flex flex-col gap-4">
                {productPageProps?.product.addons.map(globalAddon => (
                  <AddonComponent
                    key={`globalAddon${camelCase(globalAddon.title)}`}
                    addon={globalAddon}
                  />
                ))}

                {selectedVariantAddons.map(variantAddon => (
                  <AddonComponent
                    key={`variantAddon${camelCase(variantAddon.title)}`}
                    addon={variantAddon}
                  />
                ))}

                {productPageProps?.product.crossSellsFrom &&
                  productPageProps.product.crossSellsFrom.length > 0 && (
                    <div className="mt-5">
                      <Heading>Recommended to go with your booking</Heading>
                      <p>
                        Add more options to your booking below. You will be
                        asked to configure any further options before checkout.
                      </p>
                      {productPageProps.product.crossSellsFrom.map(
                        (crossSellFrom, crossSellFromIndex) =>
                          crossSellFrom.crossSellTo?.title && (
                            <Switch
                              key={crossSellFrom.id}
                              label={`${
                                crossSellFrom.crossSellTo.title
                              } - Overall price will be discounted by ${
                                crossSellFrom.discountType === "PERCENTAGE"
                                  ? (crossSellFrom.discountPercentage || 0) +
                                    "%"
                                  : formatAmountForDisplay(
                                      crossSellFrom.discountAmount || 0,
                                      "gbp"
                                    )
                              }`}
                              onClick={() => {
                                const currentValue = rhf.getValues(
                                  `crossSells.${crossSellFromIndex}`
                                )
                                const valueToSet = {
                                  id: crossSellFrom.id, // <-- The crossSell ID
                                  crossSellFromId:
                                    crossSellFrom.crossSellFromId, // <-- The product id - same as productPageProps.product.id
                                  crossSellFromTitle:
                                    productPageProps.product.title,
                                  crossSellToId: crossSellFrom.crossSellToId, // <-- The crossSellTo product id
                                  crossSellToTitle:
                                    crossSellFrom.crossSellTo?.title || "",
                                  hasCompleted: false, // <-- Whether the user has completed the crossSell. Always set to false when adding initially
                                  uuid: uuidv4() // <-- A unique ID for the crossSell. Used to identify the crossSell in the cart as can the same crossSell id can be found multiple times
                                }

                                // if the currentValue is the same as valueToSet except for the uuid, unregister it
                                if (
                                  isEqual(
                                    {
                                      ...valueToSet,
                                      uuid: currentValue?.uuid // <-- Can be empty if its not registered. TODO improve types
                                    },
                                    currentValue
                                  )
                                ) {
                                  rhf.setValue("crossSells", [
                                    ...rhf
                                      .getValues("crossSells")
                                      .slice(0, crossSellFromIndex),
                                    ...rhf
                                      .getValues("crossSells")
                                      .slice(crossSellFromIndex + 1)
                                  ])
                                }

                                // if the value is different, set it
                                else {
                                  rhf.setValue(
                                    `crossSells.${crossSellFromIndex}`,
                                    valueToSet
                                  )
                                }
                              }}
                              checked={
                                !!rhf.getValues(
                                  `crossSells.${crossSellFromIndex}`
                                )
                              }
                            />
                          )
                      )}
                    </div>
                  )}
              </div>
            )}

            {currentStep === (isCrossSell ? totalSteps : totalSteps - 1) && (
              <>
                <InstallTerms
                  variant={
                    productPageProps?.product.title
                      .toLowerCase()
                      .includes("cctv")
                      ? "cctv"
                      : "alarm"
                  }
                />
                <Switch
                  {...rhf.register("agreedTerms")}
                  label="I agree to the install terms"
                  checked={rhf.getValues("agreedTerms")}
                  onClick={() =>
                    rhf.setValue("agreedTerms", !rhf.getValues("agreedTerms"))
                  }
                />
              </>
            )}

            {!isCrossSell && currentStep === totalSteps && (
              <>
                <AvailabilityCalendar variant="frontend" />
              </>
            )}

            <div className="flex gap-1 mt-5">
              {currentStep > 0 &&
                steps[currentStep - 1].navigation !== "disabled" && (
                  <Button
                    kind="visual"
                    icon="chevron-left"
                    iconPosition="left"
                    onClick={e =>
                      setSteps({
                        event: e,
                        stepTo: currentStep - 1,
                        rhf
                      })
                    }
                  >
                    Previous
                  </Button>
                )}

              {currentStep < totalSteps &&
                rhf.formState.isValid &&
                steps[currentStep + 1].navigation !== "disabled" && (
                  <Button
                    kind="visual"
                    icon="chevron-right"
                    iconPosition="right"
                    onClick={e =>
                      setSteps({
                        event: e,
                        stepTo: currentStep + 1,
                        rhf
                      })
                    }
                  >
                    Next
                  </Button>
                )}

              {/* Show the add to cart button if is last step and installDate has been selected. */}
              {/* Otherwise if there is a crossSell, show it if it is the last step & disregard the install date */}
              {currentStep === totalSteps &&
                (isCrossSell ? true : rhf.getValues("installDate")) && (
                  <ActionButton
                    isLoading={addingToCart}
                    loadingText="Adding..."
                    onClick={async () => {
                      if (!productPageProps?.product.id) return
                      setAddingToCart(true)

                      // if this is a crossSell, set it's hasCompleted prop to true in the cart if the cart. Match by the uuid in the crossSells array in each cart item
                      // set this before add to cart as can't access state after
                      if (isCrossSell) {
                        const newCart = cart.map(cartItem => {
                          if (cartItem.crossSells) {
                            cartItem.crossSells.map(crossSell => {
                              if (crossSell.uuid === urlParams?.uuid) {
                                crossSell.hasCompleted = true
                              }
                            })
                          }
                          return cartItem
                        })
                        setCart(newCart)
                      }

                      addToCart({
                        id: uuidv4(), // <-- Unique cart item ID. Not used yet, but might be useful for other things
                        productId: productPageProps?.product.id,
                        productTitle: productPageProps?.product.title,
                        productType: "BOOKING",
                        // use the last variant id in the variants object for the variant id as that is the one that contains the relevant price
                        variantId:
                          Object.values(rhf.getValues("variant")).pop()?.id ||
                          "",
                        // join all the variant titles together to create the cart variant title
                        // if there is only one variant, dont set a variant title in the cart
                        ...(Object.values(rhf.getValues("variant")).length >
                          1 && {
                          variantTitle: Object.values(rhf.getValues("variant"))
                            .map(variant => variant.title)
                            .join(", ")
                        }),
                        addons: Object.values(rhf.getValues("addons")).map(
                          addon => ({
                            id: addon.id,
                            // if title is an array of strings, join them together
                            title: Array.isArray(addon.title)
                              ? addon.title.join(", ")
                              : addon.title,
                            type: addon.type,
                            template: addon.template,
                            quantity: addon.quantity
                          })
                        ),
                        quantity: 1,
                        installDate: rhf.getValues("installDate"),
                        crossSells: rhf.getValues("crossSells"),
                        ...(isCrossSell &&
                          urlParams?.uuid && {
                            crossSellUuid: urlParams.uuid as string
                          })
                      })

                      // wait for a second as react is too fast for the UX sometimes!
                      await new Promise(resolve =>
                        setTimeout(resolve, 1000)
                      ).then(() => {
                        setAddingToCart(false)
                        // if there are any crossSells in the rhf state, or any that are not completed in the cart, redirect to the crossSell page otherwise redirect to checkout
                        if (
                          rhf.getValues("crossSells").length > 0 ||
                          cart.some(cartItem =>
                            cartItem.crossSells?.some(
                              crossSell => !crossSell.hasCompleted
                            )
                          )
                        ) {
                          const firstCrossSell = rhf.getValues("crossSells")[0]
                          if (firstCrossSell) {
                            router.push(
                              `/products/${slugify(
                                firstCrossSell.crossSellToTitle
                              )}?crossSellId=${firstCrossSell.id}&uuid=${
                                firstCrossSell.uuid
                              }`
                            )
                          } else {
                            const firstCrossSellInCart = cart.find(
                              cartItem =>
                                cartItem.crossSells &&
                                cartItem.crossSells.length > 0
                            )?.crossSells?.[0]
                            if (firstCrossSellInCart) {
                              router.push(
                                `/products/${slugify(
                                  firstCrossSellInCart.crossSellToTitle
                                )}?crossSellId=${
                                  firstCrossSellInCart.id
                                }&uuid=${firstCrossSellInCart.uuid}`
                              )
                            } else {
                              router.push("/checkout")
                            }
                          }
                        }
                        // if no crossSells in any of the cart items or rhf.crossSells, continue to /checkout
                        else {
                          router.push("/checkout")
                        }
                      })
                    }}
                  >
                    <FaIcon icon="cart-shopping" className="mr-2" />
                    {isCrossSell ? "Add to booking" : "Book install"}
                  </ActionButton>
                )}
            </div>
          </div>
          <div
            className="mdmin:w-1/3 smmax:w-full bg-primary p-3 text-white"
            ref={rightContentRef}
          >
            <Heading variant="light" underline={false}>
              Summary
            </Heading>
            {productSteps &&
              Object.keys(rhf.watch("variant")).length === 0 &&
              currentStep < totalVariantSteps && (
                <p className="font-bold text-sm">
                  Please select options on the left and a summary will appear
                  here
                </p>
              )}
            {productSteps.map((step, stepIndex) =>
              rhf.watch(`variant.${stepIndex}`) ? (
                <div key={`step${camelCase(step.title)}`}>
                  <p className="font-bold my-0">{step.title}:</p>
                  <p className="mt-0 font-bold text-sm">
                    {rhf.watch(`variant.${stepIndex}.title`)}
                  </p>
                </div>
              ) : null
            )}
            {/* Then after the variants are displayed, summarise the addons selected */}
            {currentStep >= totalVariantSteps && (
              <>
                {productPageProps?.product.addons.map(globalAddon => (
                  <AddonSummaryItem
                    key={`globalAddon${camelCase(globalAddon.title)}`}
                    addon={globalAddon}
                  />
                ))}

                {selectedVariantAddons.map(variantAddon => (
                  <AddonSummaryItem
                    key={`variantAddon-${camelCase(variantAddon.title)}`}
                    addon={variantAddon}
                  />
                ))}
              </>
            )}
            {/* Lastly summarise the install date & totals table */}
            {currentStep === totalSteps && rhf.watch("installDate") && (
              <div>
                <p className="font-bold my-0">Install date:</p>
                <p className="mt-0 font-bold text-sm">
                  {formatDateForDisplay(rhf.watch("installDate"))}
                </p>
              </div>
            )}
            {price.variant + price.addons > 0 &&
              currentStep >= totalVariantSteps && (
                <>
                  <hr />
                  <Heading underline={false} variant="light">
                    Totals
                  </Heading>
                  <TotalsTable className="mt-0" />
                </>
              )}
          </div>
        </Container>
      </Section>
    </FormProvider>
  )
}

export default ProductTypeBooking
